import React, { useState, useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import {
  TextField,
  Button,
  Select,
  MenuItem,
  Card,
  CardContent,
  IconButton,
  Icon,
  InputLabel,
  FormControl
} from '@material-ui/core';
import Toast from 'react-hot-toast';
import Http from '../../../Http';

const LineaTransporte = () => {
  const history = useHistory();
  const { id } = useParams();

  const [clave, setClave] = useState('');
  const [nombre, setNombre] = useState('');
  const [circuitosOptions, setCircuitosOptions] = useState([]);
  const [circuitosCostos, setCircuitosCostos] = useState([]);

  const [selectedCircuitoId, setSelectedCircuitoId] = useState('');
  const [costoInput, setCostoInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchFormData();
    if (id) {
      fetchRecord(id);
    }
  }, [id]);

  const fetchFormData = async () => {
    try {
      const res = await Http.get('linea-transporte/form');
      if (!res.error && res.data) {
        setCircuitosOptions(res.data.circuitos || []);
      }
    } catch (e) {
      console.error(e);
      Toast.error('Error al cargar la lista de circuitos');
    }
  };

  const fetchRecord = async (recordId) => {
    setLoading(true);
    try {
      const res = await Http.get(`linea-transporte/one/${recordId}`);
      if (!res.error && res.data) {
        setClave(res.data.linea_transp_linea || '');
        setNombre(res.data.linea_transp_nombre || '');
        setCircuitosCostos(res.data.circuitos || []);
      } else {
        Toast.error('No se pudo encontrar el registro');
      }
    } catch (e) {
      console.error(e);
      Toast.error('Error al obtener la información de la línea de transporte');
    } finally {
      setLoading(false);
    }
  };

  const handleAddCircuitoCosto = () => {
    if (!selectedCircuitoId) {
      Toast.error('Por favor seleccione un circuito');
      return;
    }
    if (!costoInput || isNaN(costoInput) || Number(costoInput) < 0) {
      Toast.error('Por favor ingrese un costo válido');
      return;
    }

    const circuitoObj = circuitosOptions.find(c => Number(c.id) === Number(selectedCircuitoId));
    const circuitoNombre = circuitoObj ? circuitoObj.circuito : `Circuito #${selectedCircuitoId}`;

    const existsIndex = circuitosCostos.findIndex(c => Number(c.id_circuito) === Number(selectedCircuitoId));
    if (existsIndex >= 0) {
      const updated = [...circuitosCostos];
      updated[existsIndex] = {
        ...updated[existsIndex],
        costo: Number(costoInput),
        circuito_nombre: circuitoNombre
      };
      setCircuitosCostos(updated);
      Toast.success('Costo del circuito actualizado en la lista');
    } else {
      setCircuitosCostos([
        ...circuitosCostos,
        {
          id_circuito: Number(selectedCircuitoId),
          costo: Number(costoInput),
          circuito_nombre: circuitoNombre
        }
      ]);
    }

    setSelectedCircuitoId('');
    setCostoInput('');
  };

  const handleRemoveCircuito = (index) => {
    const updated = circuitosCostos.filter((_, i) => i !== index);
    setCircuitosCostos(updated);
  };

  const handleSave = async () => {
    if (!clave.trim()) {
      Toast.error('La clave es obligatoria');
      return;
    }
    if (!nombre.trim()) {
      Toast.error('El nombre es obligatorio');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        id: id || null,
        linea_transp_linea: clave,
        linea_transp_nombre: nombre,
        circuitos: circuitosCostos
      };

      const res = await Http.post('linea-transporte/save', payload);
      setSaving(false);

      if (!res.error) {
        Toast.success('Línea de transporte guardada con éxito');
        history.push('/Catalogo/LineasTransportes');
      } else {
        Toast.error(res.errMsg || 'Error al guardar la línea de transporte');
      }
    } catch (e) {
      setSaving(false);
      console.error(e);
      Toast.error('Error de servidor al guardar los cambios');
    }
  };

  if (loading) {
    return <div className="p-6 text-center text-gray-500">Cargando...</div>;
  }

  return (
    <div className="w-full max-w-4xl mx-auto p-4 md:p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900 tracking-tight">
          {id ? 'Editar Línea de Transporte' : 'Nueva Línea de Transporte'}
        </h1>
        <Button
          variant="outlined"
          color="default"
          onClick={() => history.push('/Catalogo/LineasTransportes')}
        >
          Volver al Listado
        </Button>
      </div>

      <Card variant="outlined" className="bg-white rounded-xl shadow-sm border border-gray-200">
        <CardContent className="p-6 space-y-4">
          <h2 className="text-lg font-semibold text-gray-800 border-b pb-2">Datos Generales</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
            <TextField
              label="Clave *"
              variant="outlined"
              size="small"
              value={clave}
              onChange={(e) => setClave(e.target.value)}
              fullWidth
            />
            <TextField
              label="Nombre *"
              variant="outlined"
              size="small"
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              fullWidth
            />
          </div>
        </CardContent>
      </Card>

      <Card variant="outlined" className="bg-white rounded-xl shadow-sm border border-gray-200">
        <CardContent className="p-6 space-y-4">
          <h2 className="text-lg font-semibold text-gray-800 border-b pb-2">Costos del Viaje por Circuito</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end pt-2">
            <div className="md:col-span-6">
              <FormControl variant="outlined" size="small" fullWidth>
                <InputLabel id="circuito-label">Seleccionar Circuito</InputLabel>
                <Select
                  labelId="circuito-label"
                  label="Seleccionar Circuito"
                  value={selectedCircuitoId}
                  onChange={(e) => setSelectedCircuitoId(e.target.value)}
                >
                  <MenuItem value="">
                    <em>Seleccione...</em>
                  </MenuItem>
                  {circuitosOptions.map((c) => (
                    <MenuItem key={`circ-opt-${c.id}`} value={c.id}>
                      {c.circuito}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>

            <div className="md:col-span-4">
              <TextField
                label="Costo ($)"
                variant="outlined"
                size="small"
                type="number"
                inputProps={{ step: "0.01", min: "0" }}
                value={costoInput}
                onChange={(e) => setCostoInput(e.target.value)}
                fullWidth
              />
            </div>

            <div className="md:col-span-2">
              <Button
                variant="contained"
                color="primary"
                onClick={handleAddCircuitoCosto}
                className="w-full h-10 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-lg"
              >
                + Añadir
              </Button>
            </div>
          </div>

          <div className="pt-4">
            <div className="overflow-x-auto border border-gray-200 rounded-lg">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">Circuito</th>
                    <th className="px-4 py-3 text-right font-medium text-gray-600">Costo del Viaje</th>
                    <th className="px-4 py-3 text-center font-medium text-gray-600">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {circuitosCostos.length > 0 ? (
                    circuitosCostos.map((item, idx) => (
                      <tr key={`item-costo-${idx}`} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-gray-900 font-medium">{item.circuito_nombre || `Circuito #${item.id_circuito}`}</td>
                        <td className="px-4 py-3 text-right text-gray-900 font-bold">
                          ${Number(item.costo).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <IconButton
                            size="small"
                            color="secondary"
                            onClick={() => handleRemoveCircuito(idx)}
                            title="Eliminar Circuito"
                          >
                            <Icon>delete</Icon>
                          </IconButton>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={3} className="px-4 py-6 text-center text-gray-400 font-normal">
                        No hay circuitos asignados a esta línea de transporte.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end gap-3 pt-2">
        <Button
          variant="outlined"
          onClick={() => history.push('/Catalogo/LineasTransportes')}
          disabled={saving}
        >
          Cancelar
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={saving}
          className="bg-blue-600 hover:bg-blue-700 text-white font-bold px-8 rounded-lg shadow-md"
        >
          {saving ? 'Guardando...' : 'Guardar Cambios'}
        </Button>
      </div>
    </div>
  );
};

export default LineaTransporte;
